import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'

import * as lodash from 'lodash'
import { debounceTime } from 'rxjs/operators'
import { ContractTemplate, emailSubjectByTemplateType, SignFieldDef } from '@tokeet-frontend/signature'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'

export function createSendContractInfoForm(formBuilder: FormBuilder) {
  return formBuilder.group({
    name: [undefined, [Validators.required]],
    title: [undefined, [Validators.required]],
    message: [],
    inquiry: [undefined, []],
    fillFields: formBuilder.array([]),
  })
}

export function setSendContractInfosFormData(form: FormGroup, template: ContractTemplate) {
  form.patchValue({
    name: template.name,
    title: template.title || emailSubjectByTemplateType[template.type],
    message: template.message,
  })
}

@Component({
  selector: 'app-send-contract-info-form',
  templateUrl: './send-contract-info-form.component.html',
  styleUrls: ['./send-contract-info-form.component.scss'],
})
export class SendContractInfoFormComponent extends Destroyable implements OnInit, OnChanges {
  @Input() form: FormGroup
  @Input() isInquiryRequired: boolean = false
  @Input() isDocument = false
  @Input() signInApp = false

  @Input() fillBeforeSendingFields: SignFieldDef[] = []

  bindBookingObject = (r: Inquiry) => r

  get fillFieldsArray() {
    return this.form?.get('fillFields') as FormArray
  }

  constructor() {
    super()
  }

  ngOnInit(): void {
    if (this.signInApp) {
      this.form?.get('title').clearValidators()
    } else {
      this.form?.get('title').setValidators([Validators.required])
    }
    this.fillFieldsArray.valueChanges.pipe(untilDestroy(this), debounceTime(500)).subscribe((values) => {
      lodash.forEach(values, (v, i) => {
        const field = this.fillBeforeSendingFields[i]
        field.value = v
      })
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fillBeforeSendingFields']) {
      this.setFillFields()
    }
  }

  documentTypePlaceholder() {
    return this.isDocument ? $localize`Document Name` : $localize`Template Name`
  }

  inquiryTooltip() {
    return this.isDocument
      ? $localize`Select a booking that will be used to fill any rental or inquiry data fields when this document is sent.`
      : $localize`Select a booking that will be used to fill any rental or inquiry data fields when this template is sent.`
  }

  emailSubjectTooltip() {
    return this.isDocument
      ? $localize`Enter a custom email subject for the email that will be used to send this document.`
      : $localize`Enter a custom email subject for the email that will be used to send this template.`
  }

  emailMessageTooltip() {
    return this.isDocument
      ? $localize`Enter a custom email message for the email that will be used to send this document.`
      : $localize`Enter a custom email message for the email that will be used to send this template.`
  }

  customFieldPlaceholder(index: number) {
    return this.fillBeforeSendingFields[index]?.label || $localize`Custom Field` + ' ' + (index + 1)
  }

  setFillFields() {
    const fillFieldsArray = this.form.get('fillFields') as FormArray
    fillFieldsArray.clear()
    lodash.forEach(this.fillBeforeSendingFields, (field) => {
      fillFieldsArray.push(new FormControl(field.value, [Validators.required]))
    })
  }

  onResetInquiry() {
    this.form.patchValue({ inquiry: undefined })
  }
}
